import React, { useState, useEffect, useCallback } from "react";
import { Button, Typography, DatePicker } from "antd";
import { ReloadOutlined, BarChartOutlined, FilterOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import axiosInstance from "../../../core/services/axiosConfig";
import { apis } from "../../../core/services/params";
import StitchLayout from "../../components/Crm/CrmLayout";
import AgentPerformanceTab from "../../components/Crm/AgentPerformanceTab";
import AgentComparisonSection from "../../components/Crm/AgentComparisonSection";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const CALL_TYPES = [
    { label: "InBound", value: "InBound" },
    { label: "Manual", value: "Manual" },
];

// Quick date filter pills — same pattern as Support Dashboard
const QUICK_FILTERS = [
    { label: "Today", days: 0 },
    { label: "Yesterday", days: -1 },
    { label: "Last 3 Days", days: 3 },
    { label: "Last 7 Days", days: 7 },
    { label: "Last 14 Days", days: 14 },
    { label: "Last 30 Days", days: 30 },
    { label: "Last 90 Days", days: 90 },
];

const AgentPerformancePage = () => {
    const [agentWiseReport, setAgentWiseReport] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [agentProfiles, setAgentProfiles] = useState<any[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useState(false);

    const [todayReport, setTodayReport] = useState<any[]>([]);
    const [yesterdayReport, setYesterdayReport] = useState<any[]>([]);
    const [loadingToday, setLoadingToday] = useState(false);
    const [loadingYesterday, setLoadingYesterday] = useState(false);

    const [pickerValue, setPickerValue] = useState<any[]>(() => {
        try {
            const stored = sessionStorage.getItem('agent_perf_picker_value');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.length === 2 && parsed[0] && parsed[1]) {
                    return [dayjs(parsed[0]), dayjs(parsed[1])];
                }
            }
        } catch {}
        return [dayjs().startOf("day"), dayjs()];
    });
    // Type filter — InBound or Manual, default InBound
    const [callType, setCallType] = useState<string>(() => {
        return sessionStorage.getItem('agent_perf_call_type') || "InBound";
    });
    // Active quick filter label for pill highlight state
    const [activeQuickFilter, setActiveQuickFilter] = useState<string>(() => {
        return sessionStorage.getItem('agent_perf_quick_filter') || "Today";
    });
    const [saharaTicketsCount, setSaharaTicketsCount] = useState<number | null>(null);

    const fetchReport = useCallback(async (start: string, end: string, type: string = "InBound") => {
        setLoading(true);
        try {
            const response = await axiosInstance.post(
                `${apis.OZONETEL_LIVE_REPORT}?page=1&per_page=1`,
                { filters: { start_date: start, end_date: end, Type: type } }
            );
            if (response.data?.agent_wise_report) {
                setAgentWiseReport(response.data.agent_wise_report);
            }
        } catch (err) {
            console.error("Failed to fetch agent performance report", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAgentProfiles = useCallback(async (start: string, end: string, type: string = "InBound") => {
        setLoadingProfiles(true);
        try {
            const startDate = start.split("T")[0];
            const endDate = end.split("T")[0];
            const response = await axiosInstance.get(
                `${apis.OZONETEL_AGENT_LEADERBOARD}?start_date=${startDate}&end_date=${endDate}&type=${type}`
            );
            if (response.data?.data) {
                setAgentProfiles(response.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch agent profiles", err);
        } finally {
            setLoadingProfiles(false);
        }
    }, []);

    const fetchComparison = useCallback(async (type: string = "InBound") => {
        const nowTime = moment().format("HH:mm:ss");
        const todayS = moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const todayE = moment().format(`YYYY-MM-DDT${nowTime}`);
        const yestS = moment().subtract(1, "day").startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const yestE = moment().subtract(1, "day").format(`YYYY-MM-DDT${nowTime}`);

        setLoadingToday(true);
        setLoadingYesterday(true);
        try {
            const [todayRes, yestRes] = await Promise.all([
                axiosInstance.post(`${apis.OZONETEL_LIVE_REPORT}?page=1&per_page=1`, { filters: { start_date: todayS, end_date: todayE, Type: type } }),
                axiosInstance.post(`${apis.OZONETEL_LIVE_REPORT}?page=1&per_page=1`, { filters: { start_date: yestS, end_date: yestE, Type: type } }),
            ]);
            if (todayRes.data?.agent_wise_report) setTodayReport(todayRes.data.agent_wise_report);
            if (yestRes.data?.agent_wise_report) setYesterdayReport(yestRes.data.agent_wise_report);
        } catch (err) {
            console.error("Failed to fetch comparison data", err);
        } finally {
            setLoadingToday(false);
            setLoadingYesterday(false);
        }
    }, []);

    const fetchSaharaTicketsCount = useCallback(async (start: string, end: string) => {
        try {
            // Need to convert YYYY-MM-DDTHH:mm:ss to ISO format required by the API, e.g. YYYY-MM-DDTHH:mm:ss.000Z
            // The Ozonetel filters uses local string, but Freshdesk API uses proper ISO strings
            const startIso = moment(start).utc().toISOString();
            const endIso = moment(end).utc().toISOString();

            const response = await axiosInstance.post(
                `${apis.FRESHDESK_TICKETS_LIST}?page=1&per_page=1`,
                {
                    filters: {
                        agent_name: ["Sahara Voice Bot"],
                        created_at_start: startIso,
                        created_at_end: endIso
                    }
                }
            );
            if (response.data?.status && response.data?.pagination) {
                setSaharaTicketsCount(response.data.pagination.total_count);
            } else {
                setSaharaTicketsCount(0);
            }
        } catch (err) {
            console.error("Failed to fetch Sahara tickets count", err);
            setSaharaTicketsCount(null);
        }
    }, []);

    const handleRangeChange = (vals: any) => {
        if (!vals || !vals[0] || !vals[1]) return;
        setPickerValue(vals);
        sessionStorage.setItem('agent_perf_picker_value', JSON.stringify([vals[0].toISOString(), vals[1].toISOString()]));
        setActiveQuickFilter(""); // clear quick filter when user manually picks a range
        sessionStorage.removeItem('agent_perf_quick_filter');
        const s = vals[0].format("YYYY-MM-DDTHH:mm:ss");
        const e = vals[1].format("YYYY-MM-DDTHH:mm:ss");
        fetchReport(s, e, callType);
        fetchAgentProfiles(s, e, callType);
        fetchSaharaTicketsCount(s, e);
    };

    // Quick filter handler — mirrors Support Dashboard logic
    const handleQuickFilter = (filter: typeof QUICK_FILTERS[0]) => {
        let start: ReturnType<typeof dayjs>;
        let end: ReturnType<typeof dayjs>;
        if (filter.days === 0) {
            start = dayjs().startOf("day");
            end = dayjs();
        } else if (filter.days === -1) {
            start = dayjs().subtract(1, "day").startOf("day");
            end = dayjs().subtract(1, "day").endOf("day");
        } else {
            start = dayjs().subtract(filter.days - 1, "day").startOf("day");
            end = dayjs();
        }
        setPickerValue([start, end]);
        sessionStorage.setItem('agent_perf_picker_value', JSON.stringify([start.toISOString(), end.toISOString()]));
        setActiveQuickFilter(filter.label);
        sessionStorage.setItem('agent_perf_quick_filter', filter.label);
        const s = start.format("YYYY-MM-DDTHH:mm:ss");
        const e = end.format("YYYY-MM-DDTHH:mm:ss");
        fetchReport(s, e, callType);
        fetchAgentProfiles(s, e, callType);
        fetchSaharaTicketsCount(s, e);
    };

    const handleRefresh = () => {
        if (!pickerValue[0] || !pickerValue[1]) return;
        const s = pickerValue[0].format("YYYY-MM-DDTHH:mm:ss");
        const e = pickerValue[1].format("YYYY-MM-DDTHH:mm:ss");
        fetchReport(s, e, callType);
        fetchAgentProfiles(s, e, callType);
        fetchSaharaTicketsCount(s, e);
    };

    const handleResetFilters = () => {
        const start = dayjs().startOf("day");
        const end = dayjs();
        
        setPickerValue([start, end]);
        sessionStorage.removeItem('agent_perf_picker_value');
        
        setCallType("InBound");
        sessionStorage.removeItem('agent_perf_call_type');
        
        setActiveQuickFilter("Today");
        sessionStorage.setItem('agent_perf_quick_filter', "Today");
        
        const s = start.format("YYYY-MM-DDTHH:mm:ss");
        const e = end.format("YYYY-MM-DDTHH:mm:ss");
        
        fetchReport(s, e, "InBound");
        fetchAgentProfiles(s, e, "InBound");
        fetchComparison("InBound");
        fetchSaharaTicketsCount(s, e);
    };

    // When callType changes, re-fetch with current date range
    const handleTypeChange = (val: string) => {
        setCallType(val);
        sessionStorage.setItem('agent_perf_call_type', val);
        const s = pickerValue[0]?.format("YYYY-MM-DDTHH:mm:ss") ?? dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const e = pickerValue[1]?.format("YYYY-MM-DDTHH:mm:ss") ?? dayjs().format("YYYY-MM-DDTHH:mm:ss");
        fetchReport(s, e, val);
        fetchAgentProfiles(s, e, val);
        fetchComparison(val);
        // We only pass dates to sahara tickets count
        fetchSaharaTicketsCount(s, e);
    };

    useEffect(() => {
        const s = pickerValue[0]?.format("YYYY-MM-DDTHH:mm:ss") || dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const e = pickerValue[1]?.format("YYYY-MM-DDTHH:mm:ss") || dayjs().format("YYYY-MM-DDTHH:mm:ss");
        fetchReport(s, e, callType);
        fetchAgentProfiles(s, e, callType);
        fetchComparison(callType);
        fetchSaharaTicketsCount(s, e);
    }, [fetchReport, fetchComparison, fetchAgentProfiles, fetchSaharaTicketsCount]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <StitchLayout>
            <div className="space-y-4 max-w-[1600px] mx-auto">
                <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 text-base">
                            <BarChartOutlined />
                        </div>
                        <div>
                            <Title level={5} style={{ marginBottom: 0, letterSpacing: "-0.3px" }}>Agent Performance</Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {pickerValue[0] && pickerValue[1]
                                    ? <>{dayjs(pickerValue[0]).format("DD MMM YYYY HH:mm")} – {dayjs(pickerValue[1]).format("DD MMM YYYY HH:mm")}</>
                                    : "Select a date range"
                                }
                            </Text>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Type filter — InBound / Manual pill toggle */}
                        <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 gap-0.5">
                            {CALL_TYPES.map(({ label, value }) => {
                                const isActive = callType === value;
                                const activeClass = value === "InBound"
                                    ? "bg-green-500 text-white shadow-sm border-green-500"
                                    : "bg-blue-500 text-white shadow-sm border-blue-500";
                                return (
                                    <button
                                        key={value}
                                        onClick={() => handleTypeChange(value)}
                                        className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 cursor-pointer border ${isActive
                                            ? activeClass
                                            : "bg-transparent text-gray-500 border-transparent hover:text-gray-700 hover:bg-white"
                                            }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        <RangePicker
                            showTime={{ format: 'HH:mm' }}
                            value={pickerValue as any}
                            onChange={handleRangeChange}
                            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                            allowClear={false}
                            format="DD MMM YYYY HH:mm"
                        />
                        <Button icon={<FilterOutlined />} onClick={handleResetFilters}>
                            Reset Filters
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Quick filter pills row — same design as Support Dashboard */}
                <div className="flex flex-wrap items-center gap-1.5 bg-white px-4 py-2.5 rounded-xl border border-gray-100 shadow-sm">
                    {QUICK_FILTERS.map((f) => {
                        const isActive = activeQuickFilter === f.label;
                        return (
                            <button
                                key={f.label}
                                onClick={() => handleQuickFilter(f)}
                                className="px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 cursor-pointer"
                                style={isActive ? {
                                    background: '#4f46e5',
                                    color: '#fff',
                                    borderColor: '#4f46e5',
                                    boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                                } : {
                                    background: 'transparent',
                                    color: '#6b7280',
                                    borderColor: '#e5e7eb',
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                <AgentPerformanceTab
                    agentWiseReport={agentWiseReport}
                    dateRange={[
                        pickerValue[0]?.format("YYYY-MM-DD"),
                        pickerValue[1]?.format("YYYY-MM-DD"),
                    ]}
                    loading={loading}
                    agentProfiles={agentProfiles}
                    loadingProfiles={loadingProfiles}
                    saharaTicketsCount={saharaTicketsCount}
                    callType={callType}
                />

                <AgentComparisonSection
                    todayReport={todayReport}
                    yesterdayReport={yesterdayReport}
                    loadingToday={loadingToday}
                    loadingYesterday={loadingYesterday}
                />
            </div>
        </StitchLayout>
    );
};

export default AgentPerformancePage;
